/**
 * DeciCraft 페이지
 * AI Agent 조직도 생성기
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Rocket as RocketIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import { deciCraftAPI } from '../../services/api';

const EXAMPLE_TASKS = [
  {
    title: '신규 벤더 계약 승인',
    description: `신규 IT 벤더와의 연간 서비스 계약을 검토하고 승인해야 합니다.
    
계약 규모는 약 5억원이며, 다음 사항을 검토해야 합니다:
- 기존 조달 규정 준수 여부
- 예산 범위 내 적정성 (ROI 분석 필요)
- 벤더의 보안 인증 및 SLA 조건
- 대체 벤더 대비 리스크 평가
- 1개월 내 계약 체결 가능성`,
  },
  {
    title: '신규 채용 승인',
    description: `개발팀에 신규 시니어 개발자 2명을 채용하려고 합니다.

검토 필요 사항:
- 인력 채용 규정 및 승인 절차 준수
- 예산 적합성 (연봉 총액 2억원 예상)
- 적합한 후보자 확보 가능성
- 팀 구조 변경에 따른 리스크
- 온보딩 일정 및 자원 가용성`,
  },
  {
    title: 'AI 시스템 도입',
    description: `고객 서비스 부서에 AI 챗봇 시스템을 도입하려고 합니다.

검토 사항:
- 개인정보보호법 및 AI 윤리 가이드라인 준수
- 도입 비용 약 3억원 (3년 TCO) vs 인건비 절감 효과
- 기술적 구현 가능성 및 기존 시스템 연동
- AI 오류 발생 시 고객 불만 리스크
- 6개월 내 파일럿 운영 시작 목표`,
  },
];

const DECISION_UNIT_COLORS = {
  REGULATION: 'primary',
  COST: 'warning',
  RISK: 'error',
  FEASIBILITY: 'info',
  DECISION: 'success',
};

const DECISION_UNIT_LABELS = {
  REGULATION: '규정',
  COST: '비용',
  RISK: '리스크',
  FEASIBILITY: '실현가능성',
  DECISION: '최종결정',
};

export default function DeciCraftPage() {
  const [searchParams] = useSearchParams();
  const [taskDescription, setTaskDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [savedResultsOpen, setSavedResultsOpen] = useState(false);
  const [savedResults, setSavedResults] = useState({ analysis: [], prompts: [] });
  const [saving, setSaving] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [promptExecutionResults, setPromptExecutionResults] = useState({});
  const [executingPrompt, setExecutingPrompt] = useState(null);
  const [promptDialog, setPromptDialog] = useState({ open: false, prompt: null, index: null });
  const [userQuestion, setUserQuestion] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0); // 강제 리렌더링용
  const [promptViewDialog, setPromptViewDialog] = useState({ open: false, data: null });

  // URL 파라미터에서 저장된 결과 로드
  useEffect(() => {
    const savedId = searchParams.get('saved');
    if (savedId) {
      handleLoadResult(parseInt(savedId));
    } else {
      // URL 파라미터가 없으면 localStorage에서 마지막 상태 복원
      restoreFromLocalStorage();
    }
  }, [searchParams]);

  const handleAnalyze = async () => {
    if (!taskDescription.trim()) {
      setSnackbar({ open: true, message: '업무 설명을 입력해주세요.', severity: 'warning' });
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setPromptExecutionResults({}); // 결과 초기화

    try {
      const response = await deciCraftAPI.analyze(taskDescription.trim());

      if (response.data.success) {
        setResult(response.data.data);
        setActiveTab(0);
        setSnackbar({ open: true, message: '분석이 완료되었습니다!', severity: 'success' });
        // 새로운 분석 결과를 localStorage에 저장
        saveToLocalStorage();
      } else {
        setError(response.data.error || '분석 중 오류가 발생했습니다.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || '분석 중 오류가 발생했습니다.';
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadExample = (description) => {
    setTaskDescription(description);
    setResult(null);
    setError(null);
    setPromptExecutionResults({}); // 결과 초기화
  };

  // 전체 초기화
  const handleClearAll = () => {
    if (window.confirm('모든 데이터를 초기화하시겠습니까?')) {
      setTaskDescription('');
      setResult(null);
      setError(null);
      setPromptExecutionResults({});
      setActiveTab(0);
      localStorage.removeItem('decicraft_state');
      setSnackbar({ open: true, message: '모든 데이터가 초기화되었습니다.', severity: 'info' });
    }
  };

  // 결과 저장
  const handleSave = async () => {
    if (!result) return;

    const title = taskDescription.length > 50 ? taskDescription.substring(0, 50) + '...' : taskDescription;
    
    setSaving(true);
    try {
      const inputData = { taskDescription };
      await deciCraftAPI.save(title, inputData, result);
      setSnackbar({ open: true, message: '결과가 저장되었습니다!', severity: 'success' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || '저장 중 오류가 발생했습니다.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 저장된 결과 목록 조회
  const handleLoadSavedResults = async () => {
    setSavedResultsOpen(true);
    setLoadingSaved(true);
    try {
      const response = await deciCraftAPI.getSaved();
      if (response.data.success) {
        const allResults = response.data.data.items || [];
        
        // 프롬프트와 분석 결과 분리
        const analysisResults = allResults.filter(item => !item.title.startsWith('[프롬프트]'));
        const promptResults = allResults.filter(item => item.title.startsWith('[프롬프트]'));
        
        setSavedResults({ analysis: analysisResults, prompts: promptResults });
      }
    } catch (err) {
      setSnackbar({ open: true, message: '저장된 결과를 불러오는 중 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setLoadingSaved(false);
    }
  };

  // 저장된 프롬프트 로드
  const handleLoadPrompt = async (id) => {
    try {
      console.log('프롬프트 로드 시작, ID:', id);
      const response = await deciCraftAPI.getSavedById(id);
      console.log('저장된 프롬프트 응답:', response.data);
      
      if (response.data.success) {
        const savedData = response.data.data;
        console.log('저장된 프롬프트 데이터:', savedData);
        
        // 프롬프트 데이터 구조 확인
        let promptData = null;
        if (savedData.result_data) {
          promptData = {
            agentName: savedData.input_data?.agentName || '알 수 없음',
            prompt: savedData.result_data.prompt || '프롬프트 내용 없음',
            executionResult: savedData.result_data.executionResult || null,
            savedAt: savedData.result_data.savedAt || savedData.created_at
          };
        }
        
        if (promptData) {
          // 프롬프트 보기 다이얼로그 열기
          setPromptViewDialog({ open: true, data: promptData });
          setSavedResultsOpen(false);
          setSnackbar({ 
            open: true, 
            message: `${promptData.agentName} 프롬프트가 로드되었습니다!`, 
            severity: 'success' 
          });
        } else {
          setSnackbar({ open: true, message: '프롬프트 데이터를 찾을 수 없습니다.', severity: 'error' });
        }
      } else {
        setSnackbar({ open: true, message: '저장된 프롬프트를 찾을 수 없습니다.', severity: 'error' });
      }
    } catch (err) {
      console.error('프롬프트 로드 에러:', err);
      const errorMessage = err.response?.data?.error || err.message || '프롬프트를 불러오는 중 오류가 발생했습니다.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };
  const handleLoadResult = async (id) => {
    try {
      console.log('결과 로드 시작, ID:', id);
      const response = await deciCraftAPI.getSavedById(id);
      console.log('저장된 결과 응답:', response.data);
      
      if (response.data.success) {
        const savedData = response.data.data;
        console.log('저장된 데이터:', savedData);
        
        // 결과 데이터 설정 (여러 가능한 구조 확인)
        let resultData = null;
        if (savedData.result_data) {
          resultData = savedData.result_data;
        } else if (savedData.resultData) {
          resultData = savedData.resultData;
        } else if (savedData.data) {
          resultData = savedData.data;
        }
        
        // 입력 데이터 설정
        let inputData = null;
        if (savedData.input_data) {
          inputData = savedData.input_data;
        } else if (savedData.inputData) {
          inputData = savedData.inputData;
        }
        
        console.log('처리된 결과 데이터:', resultData);
        console.log('처리된 입력 데이터:', inputData);
        
        // 상태를 한 번에 업데이트
        if (resultData) {
          setResult({ ...resultData }); // 새 객체로 생성하여 참조 변경
        }
        
        if (inputData && inputData.taskDescription) {
          setTaskDescription(inputData.taskDescription);
        }
        
        // 다른 상태들도 초기화
        setPromptExecutionResults({});
        setError(null);
        setActiveTab(0);
        setSavedResultsOpen(false);
        
        // 성공 메시지
        setSnackbar({ open: true, message: '결과가 로드되었습니다!', severity: 'success' });
        
        // 강제 리렌더링 트리거
        setForceUpdate(prev => prev + 1);
        
        // 강제 리렌더링을 위한 작은 트릭
        setTimeout(() => {
          console.log('현재 result 상태:', result);
        }, 100);
        
      } else {
        setSnackbar({ open: true, message: '저장된 결과를 찾을 수 없습니다.', severity: 'error' });
      }
    } catch (err) {
      console.error('결과 로드 에러:', err);
      const errorMessage = err.response?.data?.error || err.message || '결과를 불러오는 중 오류가 발생했습니다.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // 저장된 결과 삭제
  const handleDeleteResult = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('이 결과를 삭제하시겠습니까?')) return;

    try {
      await deciCraftAPI.deleteSaved(id);
      
      // 로컬 상태에서 삭제
      setSavedResults(prev => ({
        analysis: prev.analysis.filter(r => r.id !== id),
        prompts: prev.prompts.filter(r => r.id !== id)
      }));
      
      setSnackbar({ open: true, message: '결과가 삭제되었습니다.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: '삭제 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // 프롬프트 실행
  const handleExecutePrompt = async (prompt, index) => {
    setPromptDialog({ open: true, prompt, index });
    setUserQuestion('');
  };

  // 실제 프롬프트 실행
  const handleExecutePromptWithQuestion = async () => {
    const { prompt, index } = promptDialog;
    
    if (!userQuestion.trim()) {
      setSnackbar({ open: true, message: '질문을 입력해주세요.', severity: 'warning' });
      return;
    }

    setExecutingPrompt(index);
    setPromptDialog({ open: false, prompt: null, index: null });
    
    try {
      // 프롬프트를 실제 텍스트로 변환
      const promptText = generatePromptText(prompt);
      
      // API 호출
      const response = await deciCraftAPI.executePrompt({
        prompt: promptText,
        agentName: prompt.agentName,
        taskDescription: taskDescription,
        userQuestion: userQuestion.trim()
      });

      if (response.data.success) {
        setPromptExecutionResults(prev => ({
          ...prev,
          [index]: {
            question: userQuestion.trim(),
            result: response.data.data.result,
            timestamp: new Date().toLocaleString('ko-KR')
          }
        }));
        setSnackbar({ open: true, message: '프롬프트가 실행되었습니다!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: '프롬프트 실행 중 오류가 발생했습니다.', severity: 'error' });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || '프롬프트 실행 중 오류가 발생했습니다.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setExecutingPrompt(null);
      setUserQuestion('');
    }
  };

  // 프롬프트 저장
  const handleSavePrompt = async (prompt, index) => {
    try {
      const promptText = generatePromptText(prompt);
      const executionData = promptExecutionResults[index];
      
      await deciCraftAPI.savePrompt({
        agentName: prompt.agentName,
        prompt: promptText,
        executionResult: executionData || null
      });
      
      setSnackbar({ open: true, message: '프롬프트가 저장되었습니다!', severity: 'success' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || '프롬프트 저장 중 오류가 발생했습니다.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // localStorage에서 상태 복원
  const restoreFromLocalStorage = () => {
    try {
      const savedState = localStorage.getItem('decicraft_state');
      if (savedState) {
        const state = JSON.parse(savedState);
        
        // 저장된 시간이 24시간 이내인지 확인
        const savedTime = new Date(state.timestamp);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        
        if (hoursDiff > 24) {
          // 24시간이 지났으면 localStorage 초기화
          localStorage.removeItem('decicraft_state');
          return;
        }
        
        if (state.taskDescription) {
          setTaskDescription(state.taskDescription);
        }
        if (state.result) {
          setResult(state.result);
          setActiveTab(state.activeTab || 0);
          // 상태 복원 알림
          setSnackbar({ 
            open: true, 
            message: '이전 분석 결과가 복원되었습니다.', 
            severity: 'info' 
          });
        }
        if (state.promptExecutionResults) {
          setPromptExecutionResults(state.promptExecutionResults);
        }
      }
    } catch (error) {
      console.error('localStorage 복원 중 오류:', error);
      localStorage.removeItem('decicraft_state');
    }
  };

  // localStorage에 상태 저장
  const saveToLocalStorage = () => {
    try {
      const state = {
        taskDescription,
        result,
        activeTab,
        promptExecutionResults,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('decicraft_state', JSON.stringify(state));
      console.log('localStorage 저장됨:', state); // 디버깅용
    } catch (error) {
      console.error('localStorage 저장 중 오류:', error);
    }
  };

  // 상태가 변경될 때마다 localStorage에 저장
  useEffect(() => {
    if (result || taskDescription) {
      const timeoutId = setTimeout(() => {
        saveToLocalStorage();
      }, 1000); // 1초 디바운스
      
      return () => clearTimeout(timeoutId);
    }
  }, [taskDescription, result, activeTab, promptExecutionResults]);
  const generatePromptText = (prompt) => {
    return `# ${prompt.agentName}

## 역할
${prompt.role}

## 허용된 권한
${prompt.authority?.allowed?.map(item => `- ${item}`).join('\n') || '없음'}

## 금지된 권한
${prompt.authority?.forbidden?.map(item => `- ${item}`).join('\n') || '없음'}

## 판단 정책
${prompt.decisionPolicy?.map((item, i) => `${i + 1}. ${item}`).join('\n') || '없음'}

## 수행 프로세스
${prompt.process?.map((item, i) => `${i + 1}. ${item}`).join('\n') || '없음'}

## 출력 형식
${prompt.outputFormat?.map(item => `• ${item}`).join('\n') || '없음'}

## 업무 설명
${taskDescription}

위 정보를 바탕으로 업무를 수행하고 결과를 출력해주세요.`;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      {/* 헤더 */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <AccountTreeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            DeciCraft
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          업무 설명을 입력하면 AI가 자동으로 Agent 조직도와 Master Prompt를 생성합니다.
        </Typography>
      </Box>

      {/* 입력 섹션 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          업무 설명
        </Typography>

        {/* 예시 버튼 */}
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            예시:
          </Typography>
          {EXAMPLE_TASKS.map((example, i) => (
            <Button
              key={i}
              size="small"
              variant="outlined"
              onClick={() => handleLoadExample(example.description)}
              disabled={loading}
            >
              {example.title}
            </Button>
          ))}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={8}
          placeholder="승인이 필요한 업무를 자세히 설명해주세요.

예시: 신규 벤더와의 계약을 검토하고 승인해야 합니다. 계약 규모는 약 5억원이며, 조달 규정 준수 여부, ROI 분석, 보안 리스크 평가가 필요합니다..."
          value={taskDescription}
          onChange={(e) => setTaskDescription(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleAnalyze}
            disabled={loading || !taskDescription.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <RocketIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            {loading ? '분석 중...' : '🚀 AI 분석 시작'}
          </Button>
          
          {(result || taskDescription) && (
            <Button
              variant="outlined"
              size="large"
              onClick={handleClearAll}
              disabled={loading}
              sx={{
                minWidth: '120px',
                borderColor: '#ff6b6b',
                color: '#ff6b6b',
                '&:hover': {
                  borderColor: '#ff5252',
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                },
              }}
            >
              초기화
            </Button>
          )}
        </Box>

        {/* 결과 저장 및 내 분석 결과 버튼 */}
        {result && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                },
                boxShadow: '0 4px 15px rgba(168, 85, 247, 0.4)',
              }}
            >
              {saving ? '저장 중...' : '결과 저장'}
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={loadingSaved}
            startIcon={loadingSaved ? <CircularProgress size={20} /> : <HistoryIcon />}
            onClick={handleLoadSavedResults}
            sx={{
              borderColor: '#a855f7',
              color: '#a855f7',
              '&:hover': {
                borderColor: '#9333ea',
                backgroundColor: 'rgba(168, 85, 247, 0.1)',
              },
            }}
          >
            내 분석 결과
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* 결과 섹션 */}
      {result && (
        <Paper sx={{ p: 3 }}>
          {console.log('결과 렌더링 중, result:', result)} {/* 디버깅용 */}
          {console.log('result 타입:', typeof result)} {/* 디버깅용 */}
          {console.log('result keys:', Object.keys(result || {}))} {/* 디버깅용 */}
          
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab icon={<AssessmentIcon />} label="업무 분석" />
            <Tab icon={<BusinessIcon />} label="Agent 조직도" />
            <Tab icon={<DescriptionIcon />} label="Master Prompts" />
          </Tabs>

          <Divider sx={{ mb: 3 }} />

          {/* 업무 분석 탭 */}
          {activeTab === 0 && result.taskAnalysis && (
            <Box>
              {console.log('업무 분석 탭 렌더링, taskAnalysis:', result.taskAnalysis)}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">업무 분석 결과</Typography>
                <Chip label={result.taskAnalysis.taskId} size="small" />
              </Box>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip label={result.taskAnalysis.taskDomain} color="primary" size="small" />
                  </Box>
                  <Typography variant="body1">{result.taskAnalysis.summary}</Typography>
                </CardContent>
              </Card>

              <Typography variant="h6" gutterBottom>
                판단 단위 (Decision Units)
              </Typography>
              <Grid container spacing={2}>
                {result.taskAnalysis.decisionUnits?.map((unit) => (
                  <Grid item xs={12} sm={6} md={4} key={unit.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderColor: `${DECISION_UNIT_COLORS[unit.type] || 'default'}.main`,
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Chip
                            label={`${unit.order}`}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={DECISION_UNIT_LABELS[unit.type] || unit.type}
                            color={DECISION_UNIT_COLORS[unit.type] || 'default'}
                            size="small"
                          />
                        </Box>
                        <Typography variant="subtitle1" gutterBottom>
                          {unit.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {unit.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Agent 조직도 탭 */}
          {activeTab === 1 && result.orgChart && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Agent 조직도</Typography>
                <Chip label={result.orgChart.orgChartId} size="small" />
              </Box>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    최종 결정권자
                  </Typography>
                  <Typography variant="h6">{result.orgChart.finalDecision}</Typography>
                </CardContent>
              </Card>

              <Typography variant="h6" gutterBottom>
                Agent 구조
              </Typography>
              <List>
                {result.orgChart.orgChart?.map((agent, index) => (
                  <ListItem key={index}>
                    <Card sx={{ width: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {agent.agent}
                          </Typography>
                          <Chip label={`Level ${agent.level}`} size="small" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {agent.responsibility}
                        </Typography>
                        {agent.decisionUnitId && (
                          <Chip
                            label={`Decision Unit: ${agent.decisionUnitId}`}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Master Prompts 탭 */}
          {activeTab === 2 && (
            <Box>
              {console.log('Master Prompts 탭 활성화, activeTab:', activeTab)}
              {console.log('result.promptSet 존재 여부:', !!result.promptSet)}
              {console.log('result.promptSet:', result.promptSet)}
              
              {result.promptSet ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Master Prompts</Typography>
                    <Chip label={result.promptSet.promptSetId} size="small" />
                  </Box>

                  {result.promptSet.prompts?.map((prompt, index) => (
                <Accordion key={index} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {prompt.agentName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={executingPrompt === index ? <CircularProgress size={16} /> : <RocketIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExecutePrompt(prompt, index);
                          }}
                          disabled={executingPrompt === index}
                          sx={{ minWidth: 'auto' }}
                        >
                          {executingPrompt === index ? '실행 중...' : '실행'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<SaveIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSavePrompt(prompt, index);
                          }}
                          sx={{ minWidth: 'auto' }}
                        >
                          저장
                        </Button>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ space: 2 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          역할
                        </Typography>
                        <Typography variant="body2">{prompt.role}</Typography>
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            허용된 권한
                          </Typography>
                          <List dense>
                            {prompt.authority?.allowed?.map((item, i) => (
                              <ListItem key={i}>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            금지된 권한
                          </Typography>
                          <List dense>
                            {prompt.authority?.forbidden?.map((item, i) => (
                              <ListItem key={i}>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      </Grid>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          판단 정책
                        </Typography>
                        <List dense>
                          {prompt.decisionPolicy?.map((item, i) => (
                            <ListItem key={i}>
                              <ListItemText primary={`${i + 1}. ${item}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          수행 프로세스
                        </Typography>
                        <List dense>
                          {prompt.process?.map((item, i) => (
                            <ListItem key={i}>
                              <ListItemText primary={`${i + 1}. ${item}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          출력 형식
                        </Typography>
                        <List dense>
                          {prompt.outputFormat?.map((item, i) => (
                            <ListItem key={i}>
                              <ListItemText primary={`• ${item}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                      {/* 프롬프트 실행 결과 표시 영역 */}
                      {promptExecutionResults[index] && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            질문: {promptExecutionResults[index].question}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            실행 시간: {promptExecutionResults[index].timestamp}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                            실행 결과:
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {promptExecutionResults[index].result}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Master Prompts 데이터가 없습니다.
                  </Typography>
                  {console.log('promptSet이 없음, result:', result)}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* 프롬프트 실행 질문 다이얼로그 */}
      <Dialog
        open={promptDialog.open}
        onClose={() => setPromptDialog({ open: false, prompt: null, index: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RocketIcon color="primary" />
            {promptDialog.prompt?.agentName}에게 질문하기
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {promptDialog.prompt?.role}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="질문을 입력하세요"
            placeholder={`${promptDialog.prompt?.agentName}에게 질문할 내용을 입력하세요.

예시: 
- 이 업무에서 어떤 규정을 확인해야 하나요?
- 예상되는 리스크는 무엇인가요?
- 비용 분석 결과를 알려주세요.`}
            value={userQuestion}
            onChange={(e) => setUserQuestion(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPromptDialog({ open: false, prompt: null, index: null })}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleExecutePromptWithQuestion}
            disabled={!userQuestion.trim() || executingPrompt !== null}
            startIcon={executingPrompt !== null ? <CircularProgress size={16} /> : <RocketIcon />}
          >
            {executingPrompt !== null ? '실행 중...' : '실행'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 프롬프트 보기 다이얼로그 */}
      <Dialog
        open={promptViewDialog.open}
        onClose={() => setPromptViewDialog({ open: false, data: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="secondary" />
            저장된 프롬프트: {promptViewDialog.data?.agentName}
          </Box>
        </DialogTitle>
        <DialogContent>
          {promptViewDialog.data && (
            <Box>
              {/* 프롬프트 내용 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  📝 프롬프트 내용
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography 
                    variant="body2" 
                    component="pre" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.5
                    }}
                  >
                    {promptViewDialog.data.prompt}
                  </Typography>
                </Paper>
              </Box>

              {/* 실행 결과 (있는 경우) */}
              {promptViewDialog.data.executionResult && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom color="success.main">
                    🚀 실행 결과
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.main' }}>
                    {promptViewDialog.data.executionResult.question && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          질문: {promptViewDialog.data.executionResult.question}
                        </Typography>
                      </Box>
                    )}
                    {promptViewDialog.data.executionResult.timestamp && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          실행 시간: {promptViewDialog.data.executionResult.timestamp}
                        </Typography>
                      </Box>
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {promptViewDialog.data.executionResult.result || promptViewDialog.data.executionResult}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* 저장 정보 */}
              <Box sx={{ mt: 2, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  저장 시간: {new Date(promptViewDialog.data.savedAt).toLocaleString('ko-KR')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptViewDialog({ open: false, data: null })}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 저장된 결과 목록 다이얼로그 */}
      <Dialog
        open={savedResultsOpen}
        onClose={() => setSavedResultsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>내 분석 결과</DialogTitle>
        <DialogContent>
          {loadingSaved ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* 분석 결과 섹션 */}
              {savedResults.analysis.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    📊 분석 결과
                  </Typography>
                  <List>
                    {savedResults.analysis.map((item) => (
                      <ListItem
                        key={item.id}
                        button
                        onClick={() => handleLoadResult(item.id)}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemText
                          primary={item.title}
                          secondary={new Date(item.created_at).toLocaleString('ko-KR')}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={(e) => handleDeleteResult(item.id, e)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* 프롬프트 섹션 */}
              {savedResults.prompts.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom color="secondary">
                    🤖 저장된 프롬프트
                  </Typography>
                  <List>
                    {savedResults.prompts.map((item) => (
                      <ListItem
                        key={item.id}
                        button
                        onClick={() => handleLoadPrompt(item.id)}
                        sx={{
                          border: 1,
                          borderColor: 'secondary.main',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': {
                            backgroundColor: 'secondary.light',
                            color: 'secondary.contrastText',
                          },
                        }}
                      >
                        <ListItemText
                          primary={item.title}
                          secondary={new Date(item.created_at).toLocaleString('ko-KR')}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={(e) => handleDeleteResult(item.id, e)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* 데이터가 없을 때 */}
              {savedResults.analysis.length === 0 && savedResults.prompts.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                  저장된 결과가 없습니다.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSavedResultsOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
